import { nonEmptyString } from '../lib/format';
import { logger } from '../lib/logger';
import { useRef } from 'react';
import type {
  NegotiationState, ProofGraph, CalendarEvent, Claim, ClioTask, DamageItem, Element, Evidence, EvidenceReference,
  Fact, FactLink, Motion, StrategicNote, Template, ValenceType, WidgetPlacement,
} from '../types';
import { apiFetch, jsonInit } from '../lib/api';
import { genPoolId, deleteFactFromGraph } from '../lib/proofGraph';
import { persistTemplateWidgets } from '../lib/templates';
import type { AddUnmappedFactSubmission } from '../components/mapper/AddUnmappedFactModal';
import { sanitizeClioDocumentUrl } from '../types';

export interface UseProofGraphActionsOptions {
  matterId: string;
  proofGraph: ProofGraph | null;
  setProofGraph: (graph: ProofGraph) => void;
  templates: Template[];
  setTemplates: (templates: Template[]) => void;
  damages: DamageItem[];
  setDamages: (damages: DamageItem[]) => void;
  clioTasks: ClioTask[];
  setClioTasks: (tasks: ClioTask[] | ((prev: ClioTask[]) => ClioTask[])) => void;
  clioCalendarEvents: CalendarEvent[];
  setClioCalendarEvents: (events: CalendarEvent[] | ((prev: CalendarEvent[]) => CalendarEvent[])) => void;
  showAlert: (title: string, message: string) => void;
  showConfirm: (title: string, message: string, onConfirm: () => void) => void;
}

// Owns saveGraph (the single proof-graph persistence primitive) plus every
// fact/evidence/damages/motions/claim/thread/task/calendar CRUD handler that
// used to be loose functions in App.tsx. Grouped by domain in the return
// object rather than one flat blob, so consumers can destructure what they
// need. persistTemplateWidgets (shared/lib/templates.ts) resolves the
// duplicated template-persist tail previously shared by
// handleSaveStageLayout and handleUpdateWidgetConfig.
export function useProofGraphActions({
  matterId, proofGraph, setProofGraph,
  templates, setTemplates,
  damages, setDamages,
  clioTasks, setClioTasks,
  setClioCalendarEvents,
  showAlert, showConfirm,
}: UseProofGraphActionsOptions): UseProofGraphActionsResult {
  const pendingGraphSavesRef = useRef(new Map<string, Promise<void>>());
  const graphSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const damageSaveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const saveGraph = async (updatedGraph: ProofGraph): Promise<void> => {
    if (!proofGraph) return;
    setProofGraph(updatedGraph);
    const payload = JSON.stringify(updatedGraph);
    const requestKey = `${matterId}:${payload}`;
    const pending = pendingGraphSavesRef.current.get(requestKey);
    if (pending) return pending;

    // Preserve mutation order while keeping network/storage latency outside
    // the interaction boundary. Without serialization, two quick optimistic
    // edits can finish out of order and leave the older graph on the server.
    const request = graphSaveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const res = await apiFetch(`/api/v1/matters/${matterId}/graph`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        }, { activity: 'background' });
        if (!res.ok) throw new Error(`Graph save failed with status ${res.status}`);
      })
      .catch((err) => {
        logger.error('Failed to save proof graph:', err);
      })
      .finally(() => {
        pendingGraphSavesRef.current.delete(requestKey);
      });
    graphSaveQueueRef.current = request;
    pendingGraphSavesRef.current.set(requestKey, request);
    return request;
  };

  // ─── Fact pool handlers ─────────────────────────────────────────────────
  const handleUpdateFact = (factId: string, patch: Partial<Fact>): void => {
    if (!proofGraph) return;
    const facts = ((proofGraph.facts ?? [])).map((f: Fact) => (f.id === factId ? { ...f, ...patch } : f));
    void saveGraph({ ...proofGraph, facts });
  };

  const handleDeleteFact = (factId: string): void => {
    if (!proofGraph) return;
    void saveGraph(deleteFactFromGraph(proofGraph, factId));
  };

  const handleLinkFact = (claimIdx: number, elementIdx: number, factId: string, valence: ValenceType, strength = 5): void => {
    if (!proofGraph) return;
    const claims = [...((proofGraph.claims ?? []))];
    const claim = claims[claimIdx];
    if (!claim) return;
    const elements = [...((claim.elements ?? []))];
    const element = elements[elementIdx];
    if (!element) return;
    const existingLinks: FactLink[] = (element.fact_links ?? []);
    if (existingLinks.some((l: FactLink) => l.fact_id === factId)) return;
    elements[elementIdx] = { ...element, fact_links: [...existingLinks, { fact_id: factId, valence, strength }] };
    claims[claimIdx] = { ...claim, elements };
    void saveGraph({ ...proofGraph, claims });
  };

  const handleUnlinkFact = (claimIdx: number, elementIdx: number, factId: string): void => {
    if (!proofGraph) return;
    const claims = [...((proofGraph.claims ?? []))];
    const claim = claims[claimIdx];
    if (!claim) return;
    const elements = [...((claim.elements ?? []))];
    const element = elements[elementIdx];
    if (!element) return;
    elements[elementIdx] = { ...element, fact_links: ((element.fact_links ?? [])).filter((l: FactLink) => l.fact_id !== factId) };
    claims[claimIdx] = { ...claim, elements };
    void saveGraph({ ...proofGraph, claims });
  };

  const handleAddAndLinkFact = (claimIdx: number, elementIdx: number, fact: Fact, valence: ValenceType, strength = 5): void => {
    if (!proofGraph) return;
    const facts = [...((proofGraph.facts ?? [])), fact];
    const claims = [...((proofGraph.claims ?? []))];
    const claim = claims[claimIdx];
    if (!claim) { void saveGraph({ ...proofGraph, facts }); return; }
    const elements = [...((claim.elements ?? []))];
    const element = elements[elementIdx];
    if (!element) { void saveGraph({ ...proofGraph, facts }); return; }
    elements[elementIdx] = { ...element, fact_links: [...((element.fact_links ?? [])), { fact_id: fact.id, valence, strength }] };
    claims[claimIdx] = { ...claim, elements };
    void saveGraph({ ...proofGraph, facts, claims });
  };

  const countFactUsage = (factId: string): number => {
    if (!proofGraph) return 0;
    let count = 0;
    ((proofGraph?.claims ?? [])).forEach((c: Claim) => {
      ((c.elements ?? [])).forEach((el: Element) => {
        if (((el.fact_links ?? [])).some(l => l.fact_id === factId)) count++;
      });
    });
    return count;
  };

  // ─── Evidence pool handlers ─────────────────────────────────────────────
  // Pure resolver (no state writes) so callers linking several documents at
  // once in a single synchronous batch can dedupe against each other and
  // against the existing pool, then persist everything in one saveGraph
  // call instead of racing several stale-closure writes.
  const resolveEvidenceIds = (docs: EvidenceReference[]): { evidenceIds: string[]; newEvidence: Evidence[] } => {
    if (!proofGraph) return { evidenceIds: [], newEvidence: [] };
    const existingPool: Evidence[] = (proofGraph?.evidence ?? []);
    const byClioDocId = new Map(existingPool.map(e => [e.clio_doc_id, e.id]));
    const newEvidence: Evidence[] = [];
    const evidenceIds = docs.map(doc => {
      const existingId = byClioDocId.get(doc.clio_doc_id);
      if (existingId) return existingId;
      const created: Evidence = { id: genPoolId('ev'), clio_doc_id: doc.clio_doc_id, label: doc.label, deeplink: doc.deeplink };
      newEvidence.push(created);
      byClioDocId.set(doc.clio_doc_id, created.id);
      return created.id;
    });
    return { evidenceIds, newEvidence };
  };

  const handleAddOrReuseEvidence = (doc: EvidenceReference): string => {
    if (!proofGraph) return '';
    const { evidenceIds, newEvidence } = resolveEvidenceIds([doc]);
    if (newEvidence.length > 0) {
      void saveGraph({ ...proofGraph, evidence: [...((proofGraph.evidence ?? [])), ...newEvidence] });
    }
    return evidenceIds[0]!;
  };

  const handleUpdateEvidence = (evidenceId: string, patch: Partial<Evidence>): void => {
    if (!proofGraph) return;
    const evidence = ((proofGraph.evidence ?? [])).map((e: Evidence) => (e.id === evidenceId ? { ...e, ...patch } : e));
    void saveGraph({ ...proofGraph, evidence });
  };

  const handleUnlinkEvidenceFromFact = (factId: string, evidenceId: string): void => {
    if (!proofGraph) return;
    const facts = ((proofGraph.facts ?? [])).map((f: Fact) =>
      f.id === factId ? { ...f, evidence_ids: ((f.evidence_ids ?? [])).filter(id => id !== evidenceId) } : f
    );
    void saveGraph({ ...proofGraph, facts });
  };

  const handleDeleteEvidence = (evidenceId: string): void => {
    if (!proofGraph) return;
    const evidence = ((proofGraph.evidence ?? [])).filter((item: Evidence) => item.id !== evidenceId);
    const facts = ((proofGraph.facts ?? [])).map((fact: Fact) => ({
      ...fact,
      evidence_ids: ((fact.evidence_ids ?? [])).filter(id => id !== evidenceId),
    }));
    const motions = ((proofGraph.motions ?? [])).map((motion: Motion) => ({
      ...motion,
      evidence_links: ((motion.evidence_links ?? [])).filter(link => link.evidence_id !== evidenceId),
    }));
    void saveGraph({ ...proofGraph, evidence, facts, motions });

    // Damages live in the negotiation document, so remove the same evidence
    // reference there as part of this one permanent-delete action.
    void saveDamages(damages.map(damage => ({
      ...damage,
      evidence_ids: (damage.evidence_ids ?? []).filter(id => id !== evidenceId),
    })));
  };

  // ─── Unmapped facts (derived pool) handlers ─────────────────────────────
  const handleAddUnmappedFact = (submission: AddUnmappedFactSubmission): void => {
    if (!proofGraph) return;
    const { evidenceIds, newEvidence } = resolveEvidenceIds(submission.selectedDocs.map(doc => ({
      clio_doc_id: doc.id,
      label: doc.name,
      deeplink: sanitizeClioDocumentUrl((doc.deeplink ?? ''), doc.id),
    })));

    const newFact: Fact = {
      id: genPoolId('fact'),
      text: submission.text,
      status: submission.status,
      evidence_ids: evidenceIds,
    };
    void saveGraph({
      ...proofGraph,
      facts: [...((proofGraph.facts ?? [])), newFact],
      evidence: [...((proofGraph.evidence ?? [])), ...newEvidence],
    });
  };

  const handleDeleteUnmappedFact = (factId: string): void => {
    showConfirm('Permanently Delete Fact', 'Delete this fact immediately? This action cannot be undone.', () => {
      handleDeleteFact(factId);
    });
  };

  // ─── Damages (Proof Graph quick-add) handlers ───────────────────────────
  // Negotiation data (offers + damages) is owned by /negotiation, not the
  // proof graph blob, so these round-trip through a fresh GET before the PUT
  // to avoid clobbering offers edited from the Negotiation tab.
  const saveDamages = (updatedDamages: DamageItem[]): Promise<void> => {
    setDamages(updatedDamages);
    const request = damageSaveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const res = await apiFetch(`/api/v1/matters/${matterId}/negotiation`);
        const current = res.ok ? await res.json<NegotiationState>() : {};
        const saveResponse = await apiFetch(`/api/v1/matters/${matterId}/negotiation`, jsonInit('PUT', {
          matter_id: matterId,
          offers: (current?.offers ?? []),
          damages: updatedDamages,
          posture_override: current?.posture_override ?? null,
        }), { activity: 'background' });
        if (!saveResponse.ok) throw new Error(`Damage save failed with status ${saveResponse.status}`);
      })
      .catch((err) => {
        logger.error('Failed to save damages:', err);
      });
    damageSaveQueueRef.current = request;
    return request;
  };

  const handleAddDamage = (damage: DamageItem): void => {
    void saveDamages([...damages, damage]);
  };

  const handleDeleteDamage = (damageId: string): void => {
    void saveDamages(damages.filter(d => d.id !== damageId));
  };

  const handleUpdateDamage = (damageId: string, patch: Partial<DamageItem>): void => {
    void saveDamages(damages.map(d => d.id === damageId ? { ...d, ...patch } : d));
  };

  // ─── Motions Hub handlers ────────────────────────────────────────────────
  const handleCreateMotion = (motion: Omit<Motion, 'id'>): void => {
    if (!proofGraph) return;
    const newMotion: Motion = {
      ...motion,
      id: genPoolId('motion'),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const motions = [...((proofGraph.motions ?? [])), newMotion];
    void saveGraph({ ...proofGraph, motions });
  };

  const handleUpdateMotion = (motionId: string, patch: Partial<Motion>): void => {
    if (!proofGraph) return;
    const motions = ((proofGraph.motions ?? [])).map((m: Motion) =>
      m.id === motionId ? { ...m, ...patch, updated_at: new Date().toISOString() } : m
    );
    void saveGraph({ ...proofGraph, motions });
  };

  const handleDeleteMotion = (motionId: string): void => {
    if (!proofGraph) return;
    const motions = ((proofGraph.motions ?? [])).filter((m: Motion) => m.id !== motionId);
    void saveGraph({ ...proofGraph, motions });
  };

  // ─── Claims / threads ────────────────────────────────────────────────────
  const handleUpdateClaim = (claimIdx: number, updatedClaim: Claim): void => {
    if (!proofGraph) return;
    const newClaims = [...((proofGraph.claims ?? []))];
    newClaims[claimIdx] = updatedClaim;
    void saveGraph({ ...proofGraph, claims: newClaims });
  };

  const handleDeleteClaim = (claimId: string): void => {
    if (!proofGraph) return;
    const claims = ((proofGraph.claims ?? [])).filter((claim: Claim) => claim.id !== claimId);
    void saveGraph({ ...proofGraph, claims });

    // Claim-scoped damages and discussion threads use separate persistence
    // endpoints and must be destroyed with the claim.
    void saveDamages(damages.filter(damage => damage.claim_id !== claimId));
    apiFetch(`/api/v1/matters/${matterId}/threads/${claimId}`, { method: 'DELETE' }, { activity: 'background' })
      .catch(err => logger.error('Failed to delete claim thread:', err));
  };

  const handleUpdateThread = (claimIdx: number, updatedThread: Claim['thread']): void => {
    if (!proofGraph) return;
    // 1. Optimistically update local state immediately
    const newClaims = [...((proofGraph.claims ?? []))];
    const claim = newClaims[claimIdx];
    if (!claim) return;
    newClaims[claimIdx] = { ...claim, thread: updatedThread };
    setProofGraph({ ...proofGraph, claims: newClaims });

    // 2. Persist to the dedicated threads API (non-blocking)
    apiFetch(`/api/v1/matters/${matterId}/threads/${claim.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages: updatedThread?.messages ?? [] }),
    }, { activity: 'background' }).catch(err => logger.error('Failed to persist thread:', err));
  };

  const handleCreateClioTask = async (_messageId: string, title: string, dueDate: string, desc: string): Promise<string> => {
    const res = await apiFetch(`/api/v1/matters/${matterId}/actions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action_type: 'task',
        data: { name: title, due_date: dueDate, description: desc },
      }),
    });
    const data: unknown = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = data && typeof data === 'object' && 'message' in data && typeof data.message === 'string'
        ? data.message
        : 'The matter task could not be created.';
      throw new Error(message);
    }
    const task = data as ClioTask;
    setClioTasks(current => [...current, task]);
    return task.id;
  };

  // Toggle task status locally (simulates writing back to Clio)
  const handleToggleTaskStatus = (taskId: string): void => {
    const updated = clioTasks.map((t): ClioTask => {
      if (t.id === taskId) {
        return { ...t, status: (t.status === 'completed' ? 'pending' : 'completed') };
      }
      return t;
    });
    setClioTasks(updated);
  };

  // ─── Stage widgets / templates ──────────────────────────────────────────
  // Save the updated widgets layout for a stage to the server template config
  const handleSaveStageLayout = async (stageId: string, updatedWidgets: WidgetPlacement[]): Promise<void> => {
    if (!proofGraph) return;
    // 1. Save to matter level details (proofGraph.stage_widgets)
    const currentStageWidgets = ((proofGraph).stage_widgets ?? {});
    const updatedGraph = {
      ...proofGraph,
      stage_widgets: { ...currentStageWidgets, [stageId]: updatedWidgets },
    };
    void saveGraph(updatedGraph);

    // 2. Also save to active template
    const templateID = (nonEmptyString(proofGraph.template_id) ?? 'civil-ne');
    const updatedTemplates = await persistTemplateWidgets(templateID, (templates ?? []), (template) => ({
      ...template,
      stages: template.stages.map((stg) => (stg.id === stageId ? { ...stg, widgets: updatedWidgets } : stg)),
    }));
    if (updatedTemplates) {
      setTemplates(updatedTemplates);
    } else {
      showAlert('Error', 'Failed to save layout changes to Go server.');
    }
  };

  // Update specific widget settings/config and persist to templates JSON
  const handleUpdateWidgetConfig = async (widgetId: string, newConfig: WidgetPlacement['config']): Promise<void> => {
    if (!proofGraph) return;
    // 1. Update in matter-level stage_widgets if it exists there
    const currentStageWidgets = ((proofGraph).stage_widgets ?? {});
    let foundInStage = false;
    const updatedStageWidgets: Record<string, WidgetPlacement[]> = {};

    Object.keys(currentStageWidgets).forEach((stageId) => {
      const stageWidgets = (currentStageWidgets[stageId] ?? []);
      updatedStageWidgets[stageId] = stageWidgets.map((w: WidgetPlacement) => {
        if (w.id === widgetId) {
          foundInStage = true;
          return { ...w, config: newConfig };
        }
        return w;
      });
    });

    if (foundInStage) {
      void saveGraph({ ...proofGraph, stage_widgets: updatedStageWidgets });
    }

    // 2. Also update in templates configuration
    const templateID = (nonEmptyString(proofGraph.template_id) ?? 'civil-ne');
    const updatedTemplates = await persistTemplateWidgets(templateID, (templates ?? []), (template) => ({
      ...template,
      stages: template.stages.map((stg) => ({
        ...stg,
        widgets: ((stg.widgets ?? [])).map((w: WidgetPlacement) => (w.id === widgetId ? { ...w, config: newConfig } : w)),
      })),
    }));
    if (updatedTemplates) {
      setTemplates(updatedTemplates);
    } else {
      logger.error('Failed to save widget config to Go server.');
    }
  };

  const handleUpdateStrategicNotes = (updatedNotes: StrategicNote[]): void => {
    if (!proofGraph) return;
    void saveGraph({ ...proofGraph, strategic_notes: updatedNotes });
  };

  const handleUpdateCalendarEvents = (updatedEvents: CalendarEvent[]): void => {
    setClioCalendarEvents(updatedEvents);
    if (!proofGraph) return;
    void saveGraph({ ...proofGraph, calendar_events: updatedEvents });
  };

  const handleAddCalendarEvent = (newEvent: CalendarEvent): void => {
    setClioCalendarEvents(prev => [newEvent, ...prev]);
    if (!proofGraph) return;
    const currentEvents = ((proofGraph).calendar_events ?? []);
    const updated = [newEvent, ...currentEvents];
    void saveGraph({ ...proofGraph, calendar_events: updated });

    // Sync to Clio!
    void apiFetch(`/api/v1/matters/${matterId}/actions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action_type: 'calendar',
        data: {
          summary: newEvent.title,
          start_at: newEvent.start,
          end_at: (nonEmptyString(newEvent.end) ?? newEvent.start),
          is_all_day: !newEvent.start.includes('T'),
        },
      }),
    }, { activity: 'background' }).catch(err => logger.error('Failed to sync new event to Clio calendar:', err));
  };

  return {
    saveGraph,
    facts: {
      handleUpdateFact, handleDeleteFact, handleLinkFact, handleUnlinkFact,
      handleAddAndLinkFact, countFactUsage, handleAddUnmappedFact, handleDeleteUnmappedFact,
    },
    evidence: {
      resolveEvidenceIds, handleAddOrReuseEvidence, handleUpdateEvidence, handleUnlinkEvidenceFromFact,
      handleDeleteEvidence,
    },
    damages: {
      saveDamages, handleAddDamage, handleDeleteDamage, handleUpdateDamage,
    },
    motions: {
      handleCreateMotion, handleUpdateMotion, handleDeleteMotion,
    },
    claims: {
      handleUpdateClaim, handleDeleteClaim, handleUpdateThread,
    },
    tasks: {
      handleCreateClioTask, handleToggleTaskStatus,
    },
    templates: {
      handleSaveStageLayout, handleUpdateWidgetConfig,
    },
    misc: {
      handleUpdateStrategicNotes, handleUpdateCalendarEvents, handleAddCalendarEvent,
    },
  };
}

export interface UseProofGraphActionsResult {
    saveGraph: (updatedGraph: ProofGraph) => Promise<void>;
    facts: {
        handleUpdateFact: (factId: string, patch: Partial<Fact>) => void;
        handleDeleteFact: (factId: string) => void;
        handleLinkFact: (claimIdx: number, elementIdx: number, factId: string, valence: ValenceType, strength?: number) => void;
        handleUnlinkFact: (claimIdx: number, elementIdx: number, factId: string) => void;
        handleAddAndLinkFact: (claimIdx: number, elementIdx: number, fact: Fact, valence: ValenceType, strength?: number) => void;
        countFactUsage: (factId: string) => number;
        handleAddUnmappedFact: (submission: AddUnmappedFactSubmission) => void;
        handleDeleteUnmappedFact: (factId: string) => void;
    };
    evidence: {
        resolveEvidenceIds: (docs: EvidenceReference[]) => {
            evidenceIds: string[];
            newEvidence: Evidence[];
        };
        handleAddOrReuseEvidence: (doc: EvidenceReference) => string;
        handleUpdateEvidence: (evidenceId: string, patch: Partial<Evidence>) => void;
        handleUnlinkEvidenceFromFact: (factId: string, evidenceId: string) => void;
        handleDeleteEvidence: (evidenceId: string) => void;
    };
    damages: {
        saveDamages: (updatedDamages: DamageItem[]) => Promise<void>;
        handleAddDamage: (damage: DamageItem) => void;
        handleDeleteDamage: (damageId: string) => void;
        handleUpdateDamage: (damageId: string, patch: Partial<DamageItem>) => void;
    };
    motions: {
        handleCreateMotion: (motion: Omit<Motion, "id">) => void;
        handleUpdateMotion: (motionId: string, patch: Partial<Motion>) => void;
        handleDeleteMotion: (motionId: string) => void;
    };
    claims: {
        handleUpdateClaim: (claimIdx: number, updatedClaim: Claim) => void;
        handleDeleteClaim: (claimId: string) => void;
        handleUpdateThread: (claimIdx: number, updatedThread: Claim["thread"]) => void;
    };
    tasks: {
        handleCreateClioTask: (_messageId: string, title: string, dueDate: string, desc: string) => Promise<string>;
        handleToggleTaskStatus: (taskId: string) => void;
    };
    templates: {
        handleSaveStageLayout: (stageId: string, updatedWidgets: WidgetPlacement[]) => Promise<void>;
        handleUpdateWidgetConfig: (widgetId: string, newConfig: WidgetPlacement["config"]) => Promise<void>;
    };
    misc: {
        handleUpdateStrategicNotes: (updatedNotes: StrategicNote[]) => void;
        handleUpdateCalendarEvents: (updatedEvents: CalendarEvent[]) => void;
        handleAddCalendarEvent: (newEvent: CalendarEvent) => void;
    };
}
